// widgets/admin/AdminEditPlanDialog.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Alert,
    IconButton,
    FormControlLabel,
    Switch,
    Stack,
    InputAdornment,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RequestService from '../../services/RequestService';

const centsToReaisStr = (cents) => {
    if (cents == null) return '';
    const v = Number(cents) / 100;
    return v.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

const reaisStrToCents = (reaisStr) => {
    if (reaisStr == null || reaisStr === '') return 0;
    const normalized = reaisStr.toString().replace(/\./g, '').replace(',', '.');
    const value = Number.parseFloat(normalized || '0');
    return Math.round(value * 100);
};

const diffPlanUpdatePayload = (original, edited) => {
    const payload = {};
    const map = {
        name: 'name',
        priceCents: 'priceCents',
        aiAutoReviewEnabled: 'aiAutoReviewEnabled',
        aiReviewsPerMonth: 'aiReviewsPerMonth',
        maxUsers: 'maxUsers',
        aiTokenSoftCap: 'aiTokenSoftCap',
        active: 'active',
    };

    Object.entries(map).forEach(([key]) => {
        if (edited[key] !== original[key]) {
            payload[key] = edited[key];
        }
    });

    return payload;
};

const AdminEditPlanDialog = ({ open, onClose, plan, onSuccess }) => {
    const original = useMemo(() => plan || {}, [plan]);

    const [form, setForm] = useState({
        name: '',
        priceReais: '',
        aiAutoReviewEnabled: false,
        aiReviewsPerMonth: 0,
        maxUsers: 1,
        aiTokenSoftCap: 0,
        active: true,
    });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!plan) return;
        setForm({
            name: plan.name ?? '',
            priceReais: centsToReaisStr(plan.priceCents),
            aiAutoReviewEnabled: Boolean(plan.aiAutoReviewEnabled),
            aiReviewsPerMonth: plan.aiReviewsPerMonth ?? 0,
            maxUsers: plan.maxUsers ?? 1,
            aiTokenSoftCap: plan.aiTokenSoftCap ?? 0,
            active: Boolean(plan.active),
        });
        setError(null);
    }, [plan]);

    const handleChange = (e) => {
        const { name, value, type } = e.target;
        setForm((prev) => ({
            ...prev,
            [name]:
                type === 'number' ? (value === '' ? '' : Number(value)) : value,
        }));
    };

    const handleSwitch = (e) => {
        const { name, checked } = e.target;
        setForm((prev) => ({ ...prev, [name]: checked }));
    };

    const validate = () => {
        if (!form.name?.trim()) return 'Nome é obrigatório.';
        if (reaisStrToCents(form.priceReais) < 0)
            return 'Preço não pode ser negativo.';
        if (form.aiReviewsPerMonth < 0) return 'AI reviews/mês deve ser >= 0.';
        if (form.maxUsers < 1) return 'Max usuários deve ser >= 1.';
        if (form.aiTokenSoftCap < 0) return 'AI token soft cap deve ser >= 0.';
        return null;
    };

    const handleSubmit = async () => {
        setError(null);
        if (!plan?.id) {
            setError('Plano inválido.');
            return;
        }
        const v = validate();
        if (v) {
            setError(v);
            return;
        }

        const edited = {
            id: plan.id,
            name: form.name.trim(),
            priceCents: reaisStrToCents(form.priceReais),
            aiAutoReviewEnabled: Boolean(form.aiAutoReviewEnabled),
            aiReviewsPerMonth: Number(form.aiReviewsPerMonth || 0),
            maxUsers: Number(form.maxUsers || 1),
            aiTokenSoftCap: Number(form.aiTokenSoftCap || 0),
            active: Boolean(form.active),
        };

        const originalComparable = {
            id: original.id,
            name: original.name,
            priceCents: original.priceCents,
            aiAutoReviewEnabled: original.aiAutoReviewEnabled,
            aiReviewsPerMonth: original.aiReviewsPerMonth,
            maxUsers: original.maxUsers,
            aiTokenSoftCap: original.aiTokenSoftCap,
            active: original.active,
        };

        const payload = diffPlanUpdatePayload(originalComparable, edited);

        if (Object.keys(payload).length === 0) {
            onSuccess && onSuccess('Nenhuma alteração realizada.');
            onClose();
            return;
        }

        try {
            setSubmitting(true);
            await RequestService.updatePlan(plan.id, payload);
            onSuccess && onSuccess('Plano atualizado com sucesso!');
            onClose();
        } catch (err) {
            console.error(err);
            setError(
                'Erro ao atualizar plano. Verifique os dados e tente novamente.'
            );
        } finally {
            setSubmitting(false);
        }
    };

    const handleClose = () => {
        setError(null);
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                Editar Plano
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={{ position: 'absolute', right: 8, top: 8 }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        mt: 1,
                    }}
                >
                    <TextField
                        label="Nome"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        fullWidth
                    />
                    <TextField
                        label="Preço (R$)"
                        name="priceReais"
                        value={form.priceReais}
                        onChange={handleChange}
                        fullWidth
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    R$
                                </InputAdornment>
                            ),
                            inputMode: 'decimal',
                        }}
                        placeholder="0,00"
                    />

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <TextField
                            label="AI reviews por mês"
                            name="aiReviewsPerMonth"
                            type="number"
                            value={form.aiReviewsPerMonth}
                            onChange={handleChange}
                            fullWidth
                            inputProps={{ min: 0 }}
                        />
                        <TextField
                            label="Max usuários"
                            name="maxUsers"
                            type="number"
                            value={form.maxUsers}
                            onChange={handleChange}
                            fullWidth
                            inputProps={{ min: 1 }}
                        />
                    </Stack>

                    <TextField
                        label="AI token soft cap"
                        name="aiTokenSoftCap"
                        type="number"
                        value={form.aiTokenSoftCap}
                        onChange={handleChange}
                        fullWidth
                        inputProps={{ min: 0 }}
                    />

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={form.aiAutoReviewEnabled}
                                    onChange={handleSwitch}
                                    name="aiAutoReviewEnabled"
                                />
                            }
                            label="Revisão automática por IA"
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={form.active}
                                    onChange={handleSwitch}
                                    name="active"
                                />
                            }
                            label="Ativo"
                        />
                    </Stack>

                    {error && <Alert severity="error">{error}</Alert>}
                </Box>
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose} variant="outlined">
                    Cancelar
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={submitting}
                >
                    {submitting ? 'Salvando...' : 'Salvar alterações'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AdminEditPlanDialog;
